import styles from "./index.module.css";
import React, { useState, useEffect, useMemo } from "react";

import { ENVIRONMENT_CONFIG } from "shared/globals";
import Button, { ButtonPurpose } from "shared/Button";
import TextInput from "shared/@forms/TextInput";
import Select from "shared/@forms/Select";

import AddressLabel from "../AddressLabel";

const { supportsAddressVerification } = ENVIRONMENT_CONFIG.flags;

// Editable fields:
//  - Line1 (street)
//  - Line2 (apartment, unit, etc.)
//  - City
//  - State
//  - Country
//  - Postal Code

export default function AddressEdit({
  data,
  disabled,
  addressOptions,
  verifyAddress,
  onSubmit,
  onCancel,
}) {
  const [editData, setEditData] = useState(data);
  const [isVerified, setIsVerified] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [verifiedData, setVerifiedData] = useState(null);
  const [verifyError, setVerifyError] = useState("");

  const { countries } = addressOptions;

  const selectedCountry = useMemo(
    () => countries.find((c) => c.value === editData.country),
    [countries, editData.country]
  );

  useEffect(() => {
    setEditData(data);
    setVerifiedData(null);
    setVerifyError("");
    setIsVerified(false);
  }, [data]);

  const isDisabled = disabled || isVerifying;

  return (
    <div className={styles.container}>
      {isVerified ||
        (verifiedData == null && (
          <form
            onSubmit={async (e) => {
              e.preventDefault();

              if (isVerified || !supportsAddressVerification) {
                onSubmit(editData);
                return;
              }

              setIsVerifying(true);
              setVerifyError("");
              setVerifiedData(null);

              try {
                const verifiedAddress = await verifyAddress(editData);

                setVerifiedData({
                  ...verifiedAddress,
                  isVerified: true,
                });
              } catch (error) {
                setVerifyError(
                  error?.message || "Unable to verify the address."
                );
              } finally {
                setIsVerifying(false);
              }
            }}
          >
            <div className={styles.inputGroup}>
              <label htmlFor="recipient">Full Name (Recipient)</label>
              <TextInput
                id="recipient"
                disabled={isDisabled}
                value={editData.recipient || ""}
                onChange={(e) => {
                  setEditData({
                    ...editData,
                    recipient: e.target.value,
                  });
                }}
              />
            </div>

            <div className={styles.inputGroup}>
              <label htmlFor="line1">Street</label>
              <TextInput
                id="line1"
                required={true}
                disabled={isDisabled}
                value={editData.line1}
                onChange={(e) => {
                  setEditData({
                    ...editData,
                    line1: e.target.value,
                  });
                }}
              />
            </div>
            <div className={styles.inputGroup}>
              <label htmlFor="line2">Apartment or Unit</label>
              <TextInput
                id="line2"
                required={false}
                disabled={isDisabled}
                value={editData.line2 || ""}
                onChange={(e) => {
                  setEditData({
                    ...editData,
                    line2: e.target.value,
                  });
                }}
              />
            </div>

            <div className={styles.splitGroup}>
              <div className={styles.inputGroup}>
                <label htmlFor="city">City</label>
                <TextInput
                  id="city"
                  required={true}
                  disabled={isDisabled}
                  value={editData.city}
                  onChange={(e) => {
                    setEditData({
                      ...editData,
                      city: e.target.value,
                    });
                  }}
                />
              </div>
              <div className={styles.inputGroup}>
                <label htmlFor="state">State</label>
                <Select
                  value={editData.state}
                  disabled={isDisabled}
                  onChange={(e) => {
                    setEditData({
                      ...editData,
                      state: e.target.value,
                    });
                  }}
                >
                  {selectedCountry &&
                    selectedCountry.states.map((s, i) => (
                      <option key={`${s.value}_${i}`} value={s.value}>
                        {s.name}
                      </option>
                    ))}
                </Select>
              </div>
              <div className={styles.inputGroup}>
                <label htmlFor="postalCode">Postal Code</label>
                <TextInput
                  id="postalCode"
                  required={true}
                  disabled={isDisabled}
                  value={editData.postalCode}
                  onChange={(e) => {
                    setEditData({
                      ...editData,
                      postalCode: e.target.value,
                    });
                  }}
                />
              </div>
            </div>

            <div className={styles.inputGroup}>
              <label htmlFor="country">Country</label>
              <Select
                value={editData.country}
                disabled={isDisabled}
                onChange={(e) => {
                  setEditData({
                    ...editData,
                    country: e.target.value,
                  });
                }}
              >
                {countries.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.name}
                  </option>
                ))}
              </Select>
            </div>

            <div className={styles.actions}>
              <Button purpose={ButtonPurpose.save} disabled={isDisabled}>
                {isVerified || supportsAddressVerification === false
                  ? editData.addressId == null
                    ? "Add Address"
                    : "Update Address"
                  : "Verify Address"}
              </Button>

              <Button
                disabled={isDisabled}
                onClick={(e) => {
                  e.preventDefault();
                  onCancel();
                }}
              >
                Cancel
              </Button>
            </div>
          </form>
        ))}

      {!isVerified && (verifiedData || verifyError) && (
        <div className={styles.verifyResults}>
          <div className={styles.verifyGroup}>
            <header>
              <h3>As Entered</h3>
            </header>

            <div className={styles.verifyGroupContent}>
              <AddressLabel
                address={editData}
                addressOptions={addressOptions}
              />
            </div>
            <div className={styles.verifyGroupActions}>
              <Button
                disabled={isDisabled}
                onClick={(e) => {
                  e.preventDefault();
                  setIsVerified(false);
                  setVerifiedData(null);
                }}
              >
                Edit
              </Button>
              <Button
                purpose={ButtonPurpose.save}
                disabled={isDisabled}
                onClick={(e) => {
                  e.preventDefault();
                  onSubmit({ ...editData, isVerified: false });
                }}
              >
                Use as Entered
              </Button>
            </div>
          </div>

          <div className={styles.verifyGroup}>
            <header>
              <h3>Verified</h3>
            </header>

            <div
              className={
                verifyError
                  ? `${styles.verifyGroupContent} ${styles.verifyGroupContentFailed}`
                  : styles.verifyGroupContent
              }
            >
              {verifyError ? (
                <div className={styles.verifyError}>{verifyError}</div>
              ) : (
                <AddressLabel
                  address={verifiedData}
                  addressOptions={addressOptions}
                />
              )}
            </div>
            {!verifyError && (
              <div className={styles.verifyGroupActions}>
                <Button
                  purpose={ButtonPurpose.save}
                  disabled={isDisabled}
                  onClick={(e) => {
                    e.preventDefault();
                    onSubmit(verifiedData);
                  }}
                >
                  Use Verified
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
